import React, { useEffect, useRef, useState } from 'react';
import { useIsDesktop } from '@firsttable/web-theme';
import IconList from '@firsttable/web-components/atoms/Icon/IconList';
import IconMapM from '@firsttable/web-components/atoms/Icon/IconMapM';
import IconFilter from '@firsttable/web-components/atoms/Icon/IconFilter';
import MobileFilterBtnBlock from '@firsttable/web-components/molecules/MobileFilterBtnBlock/MobileFilterBtnBlock';
import { type RestaurantTagType } from '@discover-data/stores/restaurant/gql/fragments/restaurantTag';
import { type SuburbType } from '@discover-data/gql/fragments/suburb';
import { type PeopleOption } from '@discover-data/stores/hooks/people/options';
import { useStoreState } from '@discover-data/stores/restaurant/hooks';
import useDate from '@discover-data/stores/hooks/date/useDate';
import useSession from '@discover-data/stores/hooks/session/useSession';
import useShouldShowFavouriteFilter from '@discover-data/stores/hooks/favourite/useShouldShow';
import DiscountOnlySwitch from '@discover-components/restaurant/filters/discountOnly/switch';
import ExitRegularOnlyButton from '@discover-components/restaurant/list/exitRegularTabeOnlyButton';
import FavouriteOnlySwitch from '@discover-components/restaurant/filters/favouriteOnly/switch';
import FiltersAccordion from '@discover-components/restaurant/filters/accordion';
import Separator from '@discover-ui/separator';
import Dots from '@discover-ui/dots';
import AnalyticsButton from '@/components/analytics/components/Button';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import { useCloseModal } from '@/components/context/ModalContext';
import useMapQueryState from './useMapQueryState';
import RestaurantMap from './map';

const CloseMapButton = () => {
  const [, setMapState] = useMapQueryState();
  return (
    <TrackProvider button_name="toggle map/list" button_type="navigation">
      <AnalyticsButton
        className="mb-2"
        Icon={IconList}
        kind="outline"
        size="l"
        wide
        onClick={() => {
          setMapState(false);
        }}>
        View list
      </AnalyticsButton>
    </TrackProvider>
  );
};

export type MapModalProps = {
  defaultViewState: ViewState;
  tags: RestaurantTagType[];
  suburbs: SuburbType[];
  peopleOptions?: PeopleOption[] | null;
  minDiners?: PeopleOption | null;
  maxDiners?: PeopleOption | null;
};

function MapModal({
  defaultViewState,
  tags,
  suburbs,
  peopleOptions,
  minDiners,
  maxDiners,
}: MapModalProps) {
  const isDesktop = useIsDesktop();
  const shouldShowFavourite = useShouldShowFavouriteFilter();
  const [date] = useDate();
  const [session] = useSession();
  const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const discoverEnabled = useStoreState((state) => state.discoverEnabled);
  const regularOnly = useStoreState((state) => state.regularOnly.value);
  const [mapOpen, setMapState] = useMapQueryState();
  const closeModal = useCloseModal();
  const hasOpenedMap = useRef(mapOpen);

  useEffect(() => {
    // The button opens the modal before its URL update completes; only close after a true-to-false change.
    if (mapOpen) hasOpenedMap.current = true;
    else if (hasOpenedMap.current) closeModal();
  }, [mapOpen, closeModal]);

  useEffect(() => {
    // Paint the modal and its close controls before mounting the map and filter contents.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setContentReady(true));
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <TrackProvider
      pageEventName="map_viewed"
      source_page="map"
      eventParams={{
        lat: 0,
        lng: 0,
        date_time: date === 'week' ? 'any' : date,
        session_selected: session,
      }}>
      <div className="flex h-full min-h-full flex-col items-stretch">
        <div className="flex min-h-0 flex-1">
          <div className="relative flex-1">
            <div className="relative flex size-full flex-col items-center justify-center">
              {contentReady ? (
                <RestaurantMap
                  defaultViewState={defaultViewState}
                  tags={tags}
                  suburbs={suburbs}
                  peopleOptions={peopleOptions}
                  minDiners={minDiners}
                  maxDiners={maxDiners}
                />
              ) : (
                <div
                  role="status"
                  className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gray-100 text-brand-blue">
                  <Dots aria-hidden="true" />
                  <span>Loading map…</span>
                </div>
              )}
            </div>
            {/* mobile filters */}
            {!isDesktop && mobileFiltersVisible && (
              <div className="absolute inset-0 z-[11] flex max-h-full flex-col gap-3 overflow-y-auto bg-white p-4 md:hidden">
                {shouldShowFavourite && (
                  <>
                    <FavouriteOnlySwitch className="mb-1" />
                    <Separator />
                  </>
                )}
                {discoverEnabled && !regularOnly && (
                  <>
                    <DiscountOnlySwitch className="mb-1" />
                    <Separator />
                  </>
                )}
                {regularOnly && (
                  <>
                    <ExitRegularOnlyButton content="Regular tables only" kind="normal" />
                    <Separator />
                  </>
                )}
                <FiltersAccordion tags={tags} />
              </div>
            )}
          </div>
          {/* desktop filters */}
          {isDesktop && (
            <div className="hidden max-h-screen min-h-full min-w-[300px] flex-col gap-5 overflow-y-auto border-l p-6 supports-[height:100dvh]:max-h-dvh md:flex">
              <CloseMapButton />
              {contentReady && (
                <>
                  <Separator />
                  {shouldShowFavourite && (
                    <>
                      <FavouriteOnlySwitch className="my-2" />
                      <Separator />
                    </>
                  )}
                  {discoverEnabled && !regularOnly && (
                    <>
                      <DiscountOnlySwitch className="my-2" />
                      <Separator />
                    </>
                  )}
                  {regularOnly && (
                    <>
                      <ExitRegularOnlyButton content="Regular tables only" kind="normal" />
                      <Separator />
                    </>
                  )}
                  <FiltersAccordion tags={tags} />
                </>
              )}
            </div>
          )}
        </div>
        {/* mobile buttons */}
        <div className="flex gap-px md:hidden">
          <MobileFilterBtnBlock
            label="View list"
            onClick={() => {
              setMapState(false);
            }}
            Icon={IconList}
          />
          <MobileFilterBtnBlock
            label={mobileFiltersVisible ? 'View map' : 'View filters'}
            Icon={mobileFiltersVisible ? IconMapM : IconFilter}
            onClick={() => {
              setMobileFiltersVisible(!mobileFiltersVisible);
            }}
          />
        </div>
      </div>
    </TrackProvider>
  );
}

export default MapModal;
